import type React from 'react';
import {useEffect} from 'react';
import {POSTER_EDITOR_NAVBAR_HEIGHT} from '@Components/poster-editor/poster-editor.types';

const SEEKBAR_TOP = 120;
const POPUP_VERTICAL_MARGIN = 30;
const BOTTOM_MARGIN = 16;
// Below this width we center the popup instead of anchoring it to the click point.
const MIN_WINDOW_WIDTH_FOR_ANCHORED_POPUP = 550;

/**
 * Keeps the popup inside the viewport (below the nav bar and above the seekbar).
 * On narrow screens it centers the popup instead of anchoring it.
 *
 * Runs on every render — the original implementation did the same, since the
 * popup's size can change as items are toggled on/off.
 */
export const usePopupPosition = (popUpRef: React.RefObject<HTMLDivElement | null>): void => {
  useEffect(() => {
    const el = popUpRef.current;
    if (!el) {
      return;
    }
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const boundingRect = el.getBoundingClientRect();

    if (!windowHeight || !windowWidth) {
      return;
    }

    const spaceBetweenNavBarAndSeekbar = windowHeight - POSTER_EDITOR_NAVBAR_HEIGHT - SEEKBAR_TOP;
    const maxHeight = spaceBetweenNavBarAndSeekbar - POPUP_VERTICAL_MARGIN;
    // eslint-disable-next-line react-compiler/react-compiler -- DOM mutation via ref inside effect is intentional
    el.style.maxHeight = `${maxHeight}px`;

    if (windowWidth > MIN_WINDOW_WIDTH_FOR_ANCHORED_POPUP) {
      // Clear any translate transform left over from the narrow-screen branch, otherwise
      // the previous -50%/-50% offsets would be reapplied on top of the anchored position.
      el.style.transform = '';
      const initialPosX = boundingRect.left;
      const initialPosY = boundingRect.top;
      let posX = initialPosX;
      let posY = initialPosY;

      if (boundingRect.bottom > windowHeight - BOTTOM_MARGIN) {
        posY = windowHeight - boundingRect.height - BOTTOM_MARGIN;
      }
      if (boundingRect.right > windowWidth) {
        posX = windowWidth - boundingRect.width;
      }

      if (initialPosX !== posX || initialPosY !== posY) {
        el.style.top = `${posY}px`;
        el.style.left = `${posX}px`;
      }
    } else {
      el.style.top = `${spaceBetweenNavBarAndSeekbar / 2 + POSTER_EDITOR_NAVBAR_HEIGHT}px`;
      el.style.left = `50%`;
      el.style.transform = 'translate(-50%, -50%)';
    }
  });
};
